import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Header } from 'components/patterns/Typography'
import { DrawerDivider } from 'components/patterns'
import { ReadOnlyParameterValue } from 'components/feature/NavigatorPanes/editors/base'
import OutputtingDataTo from 'components/feature/NavigatorPanes/OutputtingDataTo'
import { timerValue } from 'utils/timers'
import strings from 'utils/strings'
import { EvaluationIconStyled } from 'components/patterns/Icon/IconsStyled'
import ConditionSimulator from 'components/feature/Simulation/ConditionSimulator'
import LogicSetEvaluationBanner from 'components/feature/Simulation/LogicSetEvaluationBanner'
import AnimatedHourglass from 'components/feature/LogicNode/QueryHeader/AnimatedHourglass'
import LogicSetInfoModal from 'components/feature/LogicSetInfoModal'
import { LearnHowLink } from 'components/feature/LogicSetInfoModal/LogicSetInfoModal.style'
import { Icon__Info as InfoIcon } from 'components/patterns/Icon/components'
import {
  ConditionsContainer,
  GateRow,
  DelayTimerRow,
} from './LogicSetSimulator.styles'

const LogicSetSimulator = (props) => {
  const {
    description,
    uri,
    finalComplexQueryOperator,
    abComplexQueryOperator,
    cdComplexQueryOperator,
    delayActivate,
    delayDeactivate,
    conditions,
    abGateSimulation,
    cdGateSimulation,
    finalGateSimulation,
    outputting,
    countdown,
    animationPlaying,
  } = props

  const [showInfoModal, setShowInfoModal] = useState(false)

  const operators = [
    {
      label: strings.logic_gate_top,
      currentValue: abComplexQueryOperator,
      evaluation: abGateSimulation,
    },
    {
      label: strings.logic_gate_middle,
      currentValue: finalComplexQueryOperator,
      evaluation: finalGateSimulation,
    },
    {
      label: strings.logic_gate_bottom,
      currentValue: cdComplexQueryOperator,
      evaluation: cdGateSimulation,
    },
  ]

  const timers = [
    {
      value: delayActivate,
      name: 'Activate delay',
      label: strings.activate_delay,
      countdown:
        delayActivate > 0 && // has an activate delay
        countdown > 0 && // is counting down
        !outputting // this delay is counting down
          ? countdown
          : null,
    },
    {
      value: delayDeactivate,
      name: 'Deactivate delay',
      label: strings.deactivate_delay,
      className: 'input-timer-last',
      countdown:
        delayDeactivate > 0 && // has deactivate delay
        countdown > 0 && // is counting down
        outputting // this delay is counting down
          ? countdown
          : null,
    },
  ]

  return (
    <>
      <Header type="h3" className="form-title">
        {description}
      </Header>
      <LogicSetEvaluationBanner evaluation={finalGateSimulation} />
      <ConditionsContainer>
        <Header type="h4">{strings.queries}</Header>
        {conditions.map((conditionUri) => (
          <ConditionSimulator key={conditionUri} uri={conditionUri} />
        ))}
      </ConditionsContainer>
      {operators.map((operator) => (
        <GateRow key={operator.label}>
          <EvaluationIconStyled evaluation={operator.evaluation} />
          <ReadOnlyParameterValue
            label={operator.label}
            value={operator.currentValue.toUpperCase()}
          />
        </GateRow>
      ))}
      <DrawerDivider />
      {timers.map((timer) => (
        <DelayTimerRow key={timer.label}>
          <span className="icon-container">
            {timer.countdown && (
              <AnimatedHourglass playing={animationPlaying} />
            )}
          </span>
          <ReadOnlyParameterValue
            label={timer.label}
            value={timerValue(timer.value)}
          />
          {timer.countdown && (
            <span className="simValue">{timerValue(timer.countdown)}</span>
          )}
        </DelayTimerRow>
      ))}

      {/* PBI 266671 - Learn How This Logic Set Works */}
      {conditions.length > 0 && (
        <LearnHowLink onClick={() => setShowInfoModal(true)}>
          <InfoIcon
            width={16}
            height={16}
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
          />
          {strings.learn_how_logic_set_works}
        </LearnHowLink>
      )}

      <OutputtingDataTo uri={uri} />

      {/* PBI 266671 - Logic Set Info Modal */}
      <LogicSetInfoModal
        visible={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        logicSetName={description}
        conditions={conditions}
        abComplexQueryOperator={abComplexQueryOperator}
        cdComplexQueryOperator={cdComplexQueryOperator}
        finalComplexQueryOperator={finalComplexQueryOperator}
        delayActivate={delayActivate}
        delayDeactivate={delayDeactivate}
      />
    </>
  )
}

LogicSetSimulator.propTypes = {
  description: PropTypes.string,
  uri: PropTypes.string,
  finalComplexQueryOperator: PropTypes.string,
  abComplexQueryOperator: PropTypes.string,
  cdComplexQueryOperator: PropTypes.string,
  delayActivate: PropTypes.number,
  delayDeactivate: PropTypes.number,
  conditions: PropTypes.array,
  finalGateSimulation: PropTypes.bool,
  abGateSimulation: PropTypes.bool,
  cdGateSimulation: PropTypes.bool,
  outputting: PropTypes.bool,
  countdown: PropTypes.number,
  animationPlaying: PropTypes.bool,
}

export default LogicSetSimulator
